import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CloseButton from '../../atoms/CloseButton';
import editIcon from '../../../images/edit-circle.svg';
import viewDetailsIcon from '../../../images/eye.svg';
import UserCardAvatar from '../UserCardAvatar';
import MarkerIcon from '../../atoms/MarkerIcon';
import ScrollArea from '../../atoms/ScrollArea';
import CommitmentSummary from '../CommitmentSummary';
import { ICON_MODES, hasMarkerAvatarData } from '../../../utils/markerTypeConfig';
import './index.scss';

const METADATA_KEY_OVERRIDES = Object.freeze({
    startDate: 'Starts',
    endDate: 'Ends',
    description: 'Description',
});

function humanizeMetadataKey(key) {
    if (!key) {
        return '';
    }

    if (METADATA_KEY_OVERRIDES[key]) {
        return METADATA_KEY_OVERRIDES[key];
    }

    const spaced = key
        .replace(/[_-]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, (_, lower, upper) => `${lower} ${upper}`);

    return spaced
        .split(' ')
        .map((segment) => (segment ? `${segment.charAt(0).toUpperCase()}${segment.slice(1)}` : ''))
        .join(' ')
        .trim();
}

const MapPopup = ({
    id,
    title,
    typeLabel,
    authorLabel,
    author,
    createdLabel,
    created,
    visibilityLabel,
    visibility,
    metadataEntries,
    canEdit,
    editAriaLabel,
    detailAriaLabel,
    onClose,
    onEdit,
    onOpenDetails,
    unknownLabel = '—',
    showAuthorAvatar = false,
    authorAvatar = null,
    enableAvatarPreview = false,
    markerColor = null,
    iconMode = ICON_MODES.NONE,
    showAuthor = true,
    showVisibility = true,
    showCreated = true,
}) => {
    const { t } = useTranslation(undefined, { useSuspense: false });
    // Backward compatible: show avatar if showAuthorAvatar is true OR iconMode is AVATAR
    // This ensures existing callers with just showAuthorAvatar still work
    const hasAvatarData = hasMarkerAvatarData(authorAvatar);
    const shouldShowAvatar = (showAuthorAvatar || iconMode === ICON_MODES.AVATAR)
        && hasAvatarData;
    const avatarAlt = (authorAvatar?.username || author || unknownLabel || '').toString().trim();
    const avatarUsername = authorAvatar?.username
        ? authorAvatar.username.toString().trim()
        : undefined;
    const allowProfilePreview = enableAvatarPreview && Boolean(authorAvatar?.userId);
    // Only show marker icon if iconMode is MARKER_ICON and we're not showing avatar
    const shouldShowMarkerIcon = iconMode === ICON_MODES.MARKER_ICON
        && markerColor
        && !shouldShowAvatar;

    const isCommitmentValue = (value) => (
        value && typeof value === 'object' && value.kind === 'commitment'
    );

    return (
        <div className="map-popup" data-marker-id={id || ''}>
            <div className="map-popup__header">
                <div className="map-popup__titles">
                    <div className="map-popup__title-wrapper">
                        {shouldShowAvatar && (
                            <UserCardAvatar
                                userId={authorAvatar.userId}
                                username={avatarUsername}
                                alt={avatarAlt || undefined}
                                avatarUrl={authorAvatar.avatarUrl}
                                size="32px"
                                radius="25%"
                                className="map-popup__avatar"
                                enableProfilePreview={allowProfilePreview}
                            />
                        )}
                        {shouldShowMarkerIcon && (
                            <MarkerIcon
                                color={markerColor}
                                size="medium"
                                className="map-popup__marker-icon"
                                ariaLabel={t('map.popup.markerIconAria', 'Marker icon')}
                            />
                        )}
                        <span className="map-popup__title">{title}</span>
                    </div>
                    <span className="map-popup__subtitle">{typeLabel}</span>
                </div>
                <div className="map-popup__actions">
                    {typeof onOpenDetails === 'function' && (
                        <button
                            type="button"
                            className="map-popup__action map-popup__action--details"
                            aria-label={detailAriaLabel || t('map.popup.viewDetailsAria', 'Open event details')}
                            onClick={onOpenDetails}
                        >
                            <img src={viewDetailsIcon} alt="" aria-hidden="true" />
                        </button>
                    )}
                    {canEdit && typeof onEdit === 'function' && (
                        <button
                            type="button"
                            className="map-popup__action map-popup__action--edit"
                            aria-label={editAriaLabel || t('map.popup.editMarkerAria', 'Edit marker')}
                            onClick={onEdit}
                        >
                            <img src={editIcon} alt="" aria-hidden="true" />
                        </button>
                    )}
                    <CloseButton
                        onClick={onClose}
                        ariaLabel={t('map.popup.closeAria', 'Close marker details')}
                        className="map-popup__close"
                    />
                </div>
            </div>

            <ScrollArea className="map-popup__content" ariaLabel={t('map.popup.contentAria', 'Marker details')}>
                <div className="map-popup__meta">
                    {showAuthor && (
                        <div className="map-popup__meta-row">
                            <span className="map-popup__meta-label">{authorLabel}</span>
                            <span className="map-popup__meta-value">{author || unknownLabel}</span>
                        </div>
                    )}
                    {showVisibility && (
                        <div className="map-popup__meta-row">
                            <span className="map-popup__meta-label">{visibilityLabel}</span>
                            <span className="map-popup__meta-value">{visibility || unknownLabel}</span>
                        </div>
                    )}
                    {showCreated && (
                        <div className="map-popup__meta-row">
                            <span className="map-popup__meta-label">{createdLabel}</span>
                            <span className="map-popup__meta-value">{created || unknownLabel}</span>
                        </div>
                    )}
                </div>

                {Array.isArray(metadataEntries) && metadataEntries.length > 0 && (
                    <dl className="map-popup__metadata">
                        {metadataEntries.map(([key, value], index) => {
                            const isCommitment = isCommitmentValue(value);
                            const rowClass = isCommitment
                                ? 'map-popup__metadata-row map-popup__metadata-row--commitment'
                                : 'map-popup__metadata-row';
                            return (
                                <div key={`${key}-${index}`} className={rowClass}>
                                    <dt>{humanizeMetadataKey(key)}</dt>
                                    <dd>
                                        {isCommitment
                                            ? <CommitmentSummary value={value.raw} className="map-popup__commitment" />
                                            : String(value)}
                                    </dd>
                                </div>
                            );
                        })}
                    </dl>
                )}
            </ScrollArea>
        </div>
    );
};

MapPopup.propTypes = {
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    typeLabel: PropTypes.string.isRequired,
    authorLabel: PropTypes.string.isRequired,
    author: PropTypes.string,
    createdLabel: PropTypes.string.isRequired,
    created: PropTypes.string,
    visibilityLabel: PropTypes.string.isRequired,
    visibility: PropTypes.string,
    metadataEntries: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.shape({
            kind: PropTypes.string,
            raw: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        }),
    ]))),
    canEdit: PropTypes.bool,
    editAriaLabel: PropTypes.string,
    detailAriaLabel: PropTypes.string,
    onClose: PropTypes.func,
    onEdit: PropTypes.func,
    onOpenDetails: PropTypes.func,
    unknownLabel: PropTypes.string,
    showAuthorAvatar: PropTypes.bool,
    authorAvatar: PropTypes.shape({
        userId: PropTypes.string,
        username: PropTypes.string,
        avatarUrl: PropTypes.string,
    }),
    enableAvatarPreview: PropTypes.bool,
    markerColor: PropTypes.string,
    iconMode: PropTypes.oneOf([ICON_MODES.AVATAR, ICON_MODES.MARKER_ICON, ICON_MODES.NONE]),
    showAuthor: PropTypes.bool,
    showVisibility: PropTypes.bool,
    showCreated: PropTypes.bool,
};

MapPopup.defaultProps = {
    id: undefined,
    author: undefined,
    created: undefined,
    visibility: undefined,
    metadataEntries: [],
    canEdit: false,
    editAriaLabel: undefined,
    detailAriaLabel: undefined,
    onClose: undefined,
    onEdit: undefined,
    onOpenDetails: undefined,
    unknownLabel: '—',
    showAuthorAvatar: false,
    authorAvatar: null,
    enableAvatarPreview: false,
    markerColor: null,
    iconMode: ICON_MODES.NONE,
    showAuthor: true,
    showVisibility: true,
    showCreated: true,
};

export default MapPopup;
export { humanizeMetadataKey };
